/**
 * Gobodo Page Layout Service
 *
 * Service layer for managing page layouts using Gobodo API.
 * Handles CRUD operations for templates, sections, fields, visibility rules, etc.
 */

import * as GobodoApi from '@gobodo/gobodo-api-promise';
import { getGobodoClient } from '../../gobodo/gobodoClient';
import GobodoConfig, { getCustomSuffix } from '../../config/gobodoConfig';
import type {
  PageLayoutTemplate,
  PageLayoutSection,
  PageLayoutField,
  Application
} from '../api/uiConfigurationApi';
import { USE_GOBODO_API } from '../../utils/gobodoUtils';
import { handleGobodoError, logGobodoError } from '../../utils/gobodoErrorHandler';
import { retryWithBackoff } from '../../utils/gobodoRetry';
import { ensureObjectTypeExists } from '../../utils/gobodoObjectTypeVerifier';
import { makeCacheOpts, type GobodoCacheOpts } from './gobodoCacheConfig';

// ============================================
// OBJECT TYPE KEYS
// ============================================

// Use dynamic suffix (e.g., __stg for staging, __c for production)
function getObjectKeys() {
  const suffix = getCustomSuffix();
  return {
    PAGE_LAYOUT_TEMPLATE: `PageLayoutTemplate${suffix}`,
    PAGE_LAYOUT_SECTION: `PageLayoutSection${suffix}`,
    PAGE_LAYOUT_FIELD: `PageLayoutField${suffix}`,
    PAGE_LAYOUT_VERSION: `PageLayoutVersion${suffix}`,
    VISIBILITY_RULE: `VisibilityRule${suffix}`,
    RELATED_LIST: `RelatedList${suffix}`,
    QUICK_ACTION: `QuickAction${suffix}`,
    LAYOUT_ASSIGNMENT: `LayoutAssignment${suffix}`,
    DYNAMIC_TAB: `DynamicTab${suffix}`
  };
}

// Lazy-load to ensure config is initialized
let _OBJECT_KEYS: ReturnType<typeof getObjectKeys> | null = null;
const OBJECT_KEYS = new Proxy({} as ReturnType<typeof getObjectKeys>, {
  get(_, prop: string) {
    if (!_OBJECT_KEYS) _OBJECT_KEYS = getObjectKeys();
    return (_OBJECT_KEYS as any)[prop];
  }
});

/** Cache duration for page layout traversals (aligns with TanStack staleTime in layoutQueries). */
/** Cache duration in seconds (gobodo-api-promise expects seconds). */
const PAGE_LAYOUT_TRAVERSAL_CACHE_SECONDS = 3 * 60;

/** gobodo-api-promise cache opts for object-type → page layout templates (relationship) traversal. */
function pageLayoutTemplatesTraversalCacheOpts(objectTypeKey: string): GobodoCacheOpts {
  return makeCacheOpts(
    ['page-layout-templates-traversal-rel', String(objectTypeKey)],
    PAGE_LAYOUT_TRAVERSAL_CACHE_SECONDS
  );
}

/** gobodo-api-promise cache opts for page layout templates by objectTypeKey (startObjectTypes + index filter) fallback traversal. */
function pageLayoutTemplatesIndexFilterTraversalCacheOpts(objectTypeKey: string): GobodoCacheOpts {
  return makeCacheOpts(
    ['page-layout-templates-traversal-index', String(objectTypeKey)],
    PAGE_LAYOUT_TRAVERSAL_CACHE_SECONDS
  );
}

/** gobodo-api-promise cache opts for layout INCOMING CONTAINER traversal (sections have parentobjectid = layout). */
function layoutSectionsTraversalCacheOpts(templateId: string): GobodoCacheOpts {
  return makeCacheOpts(
    ['page-layout-sections-traversal', String(templateId)],
    PAGE_LAYOUT_TRAVERSAL_CACHE_SECONDS
  );
}

/** gobodo-api-promise cache opts for section INCOMING CONTAINER traversal (fields have parentobjectid = section). */
function sectionFieldsTraversalCacheOpts(sectionId: string): GobodoCacheOpts {
  return makeCacheOpts(
    ['page-layout-section-fields-traversal', String(sectionId)],
    PAGE_LAYOUT_TRAVERSAL_CACHE_SECONDS
  );
}

/** Cache keys to invalidate when adding/removing/reordering section fields (for backend/gobodo-api-promise). */
function sectionFieldMutationCacheKeys(layoutId: string, sectionId: string): string[][] {
  return [
    sectionFieldsTraversalCacheOpts(sectionId).cachekey,
    layoutSectionsTraversalCacheOpts(layoutId).cachekey,
  ];
}

/** Relationship key: Object Type (as graph object) → PageLayoutTemplate (matches setup script object-type-page-layout-relationship). */
function getObjectTypePageLayoutRelKey(): string {
  return `object_type_has_page_layout${getCustomSuffix()}`;
}

/**
 * Get the graph object id for an object type (for use as parent when creating layouts linked to that type).
 * Uses getObjectTypeAsObject(id or key); returns the template response's id when present.
 */
async function getObjectTypeAsGraphObjectId(objectTypeKey: string): Promise<number | null> {
  const client = getGobodoClient();
  const objectTypesApi = new GobodoApi.GraphObjectTypesApi(client);
  try {
    const response = await retryWithBackoff(
      () => objectTypesApi.getObjectTypeAsObject({ key: objectTypeKey }),
      { maxRetries: 2 }
    );
    const id = response?.id ?? response?.data?.id;
    return id != null ? Number(id) : null;
  } catch {
    return null;
  }
}

// ============================================
// TYPE CONVERSIONS
// ============================================

/**
 * Convert PageLayoutTemplate to Gobodo record payload
 */
function templateToGobodo(template: Partial<PageLayoutTemplate>): Record<string, any> {
  return {
    name: template.name,
    description: template.description || '',
    application: template.application || 'global',
    objectTypeKey: template.objectTypeKey ?? undefined,
    isPublic: template.isPublic || false,
    isDefault: template.isDefault || false,
    version: template.version || 1,
    createdBy: template.createdBy || 'system',
    updatedBy: template.updatedBy,
    createdAt: template.createdAt || new Date().toISOString(),
    updatedAt: template.updatedAt || new Date().toISOString()
  };
}

function toIsoString(val: any): string {
  if (val == null) return new Date().toISOString();
  if (typeof val === 'string') return val;
  if (typeof val === 'object' && typeof val.toISOString === 'function') return val.toISOString();
  return new Date().toISOString();
}

function normalizeAppName(value?: string): string {
  return String(value ?? '')
    .trim()
    .toLowerCase()
    .replace(/\s+/g, '-')
    .replace(/_/g, '-');
}

/**
 * Convert Gobodo record to PageLayoutTemplate
 */
function gobodoToTemplate(gobodoRecord: any, sections: PageLayoutSection[] = []): PageLayoutTemplate {
  const data = gobodoRecord.data || gobodoRecord.properties || {};
  return {
    id: String(gobodoRecord.id),
    name: data.name || '',
    description: data.description,
    application: data.application as Application,
    objectTypeKey: data.objectTypeKey,
    isPublic: data.isPublic || false,
    isDefault: data.isDefault || false,
    sections: sections,
    version: data.version || 1,
    createdAt: toIsoString(gobodoRecord.creationdate ?? data.createdAt),
    updatedAt: toIsoString(gobodoRecord.modificationdate ?? data.updatedAt),
    createdBy: String(gobodoRecord.creatorid || data.createdBy || 'system'),
    updatedBy: data.updatedBy
  };
}

/**
 * Convert PageLayoutSection to Gobodo record payload
 */
function sectionToGobodo(section: Partial<PageLayoutSection>, templateId: string): Record<string, any> {
  return {
    templateId: templateId,
    name: section.name,
    description: section.description || '',
    page: section.page ?? 'default',
    tab: section.tab ?? 'details',
    columns: section.columns,
    order: section.order || 0,
    collapsible: section.collapsible || false,
    defaultCollapsed: section.defaultCollapsed || false,
    visible: section.visible !== false,
    createdBy: section.createdBy || 'system',
    updatedBy: section.updatedBy,
    createdAt: section.createdAt || new Date().toISOString(),
    updatedAt: section.updatedAt || new Date().toISOString()
  };
}

/**
 * Convert Gobodo record to PageLayoutSection
 */
function gobodoToSection(gobodoRecord: any, fields: PageLayoutField[] = []): PageLayoutSection {
  const data = gobodoRecord.data || gobodoRecord.properties || {};
  return {
    id: String(gobodoRecord.id),
    name: data.name || '',
    description: data.description,
    page: data.page,
    tab: data.tab,
    columns: data.columns as 1 | 2 | 3 | 4,
    order: data.order || 0,
    collapsible: data.collapsible || false,
    defaultCollapsed: data.defaultCollapsed || false,
    visible: data.visible !== false,
    fields: fields,
    createdAt: toIsoString(gobodoRecord.creationdate ?? data.createdAt),
    updatedAt: toIsoString(gobodoRecord.modificationdate ?? data.updatedAt),
    createdBy: String(gobodoRecord.creatorid || data.createdBy || 'system'),
    updatedBy: data.updatedBy
  };
}

/**
 * Convert PageLayoutField to Gobodo record payload.
 * Backend requires camelCase keys (type, fieldId, label, sectionId); we also send __c for schema.
 */
function fieldToGobodo(field: Partial<PageLayoutField>, sectionId: string): Record<string, any> {
  const sectionIdNum = /^\d+$/.test(String(sectionId)) ? parseInt(sectionId, 10) : sectionId;
  const fieldKeyVal = (field.fieldKey ?? (field as Record<string, unknown>).fieldName ?? field.fieldId ?? 'field-0').toString().trim() || 'field-0';
  const label = (field.label ?? (field as Record<string, unknown>).fieldLabel ?? '').toString().trim() || fieldKeyVal;
  const order = Number(field.order) >= 0 ? Number(field.order) : 0;
  // Keep properties minimal -- only send what Gobodo needs
  return {
    name: label,
    Name__c: label,
    fieldKey: fieldKeyVal,
    FieldKey__c: fieldKeyVal,
    fieldId: fieldKeyVal,
    FieldId__c: fieldKeyVal,
    label: label,
    Label__c: label,
    sectionId: sectionIdNum,
    SectionId__c: sectionIdNum,
    order: order,
    Order__c: order,
    type: field.type ?? 'text',
    Type__c: field.type ?? 'text',
    required: field.required ?? false,
    Required__c: field.required ?? false,
  };
}

/**
 * Convert Gobodo record to PageLayoutField (reads __c keys).
 */
function gobodoToField(gobodoRecord: any): PageLayoutField {
  const data = gobodoRecord.data || gobodoRecord.properties || {};
  const get = (k: string, alt: string) => data[k] ?? data[alt];
  return {
    fieldId: get('FieldId__c', 'fieldId') ?? get('fieldApiName', 'FieldApiName__c'),
    label: get('Label__c', 'label'),
    fieldKey: get('FieldKey__c', 'fieldKey') ?? get('fieldApiName', 'FieldApiName__c'),
    type: get('Type__c', 'type') || 'text',
    required: get('Required__c', 'required') || false,
    placeholder: get('Placeholder__c', 'placeholder'),
    order: get('Order__c', 'order') ?? 0,
    width: get('Width__c', 'width'),
    helpText: get('HelpText__c', 'helpText'),
    pii: get('Pii__c', 'pii') || false,
    encryption: get('Encryption__c', 'encryption'),
    masking: (get('Masking__c', 'masking') as any) ? (typeof (data.Masking__c ?? data.masking) === 'string' ? JSON.parse(data.Masking__c ?? data.masking) : (data.Masking__c ?? data.masking)) : undefined,
    piiConfig: (get('PiiConfig__c', 'piiConfig') as any) ? (typeof (data.PiiConfig__c ?? data.piiConfig) === 'string' ? JSON.parse(data.PiiConfig__c ?? data.piiConfig) : (data.PiiConfig__c ?? data.piiConfig)) : undefined
  };
}

// ============================================
// PAGE LAYOUT TEMPLATE OPERATIONS
// ============================================

/**
 * Fetch all page layout templates.
 * When objectTypeKey is provided: (1) tries relationship traversal from object type (getObjectTypeAsObject) OUTGOING with object_type_has_page_layout; (2) if that fails or returns nothing, uses traversal API with startObjectTypes=PageLayoutTemplate and objectTypeKey index filter (no fetch-all + client filter).
 */
export async function fetchPageLayoutTemplates(filters?: { application?: Application; objectTypeKey?: string }): Promise<{ data: PageLayoutTemplate[] }> {
  if (!USE_GOBODO_API) {
    throw new Error('Gobodo API not enabled. Set REACT_APP_USE_GOBODO_API=true');
  }

  try {
    const client = getGobodoClient();
    const objectsApi = new GobodoApi.GraphObjectsApi(client);
    let records: any[] = [];

    if (filters?.objectTypeKey) {
      const parentObjectId = await getObjectTypeAsGraphObjectId(filters.objectTypeKey);
      if (parentObjectId != null) {
        try {
          const traversalApi = new GobodoApi.GraphTraversalApi(client);
          const input = new GobodoApi.GraphTraversalInput();
          input.setGraphId(GobodoConfig.graph());
          input.setStartObjectId(parentObjectId);
          input.setRelationshipTypes([getObjectTypePageLayoutRelKey()]);
          input.setDirection(GobodoApi.GraphTraversalInput.DirectionEnum.INCOMING);
          input.setEndObjectTypes([OBJECT_KEYS.PAGE_LAYOUT_TEMPLATE]);
          input.setMaxPathLength(1);
          const cacheOpts = pageLayoutTemplatesTraversalCacheOpts(filters.objectTypeKey);
          const result = await retryWithBackoff(() => traversalApi.traverseObjects(input, cacheOpts), { maxRetries: 2 });
          records = Array.isArray(result) ? result : [];
        } catch (e: any) {
          console.warn('[fetchPageLayoutTemplates] Relationship traversal failed, using traversal + index filter:', e?.message || e);
        }
      }

      if (records.length === 0) {
        try {
          const traversalApi = new GobodoApi.GraphTraversalApi(client);
          const input = new GobodoApi.GraphTraversalInput();
          input.setGraphId(GobodoConfig.graph());
          input.setStartObjectTypes([OBJECT_KEYS.PAGE_LAYOUT_TEMPLATE]);
          input.setEndObjectTypes([OBJECT_KEYS.PAGE_LAYOUT_TEMPLATE]);
          input.setMaxPathLength(0);
          try {
            const objTypeFilter = new GobodoApi.IndexFilter();
            objTypeFilter.setIndexName('objectTypeKey');
            objTypeFilter.setFunction(GobodoApi.IndexFilter.FunctionEnum.EQUALS);
            objTypeFilter.setValues([filters.objectTypeKey]);
            input.setStartObjectFieldFilters([objTypeFilter]);
          } catch {
            // Backend may not support startObjectFieldFilters
          }
          const cacheOpts = pageLayoutTemplatesIndexFilterTraversalCacheOpts(filters.objectTypeKey);
          const result = await retryWithBackoff(() => traversalApi.traverseObjects(input, cacheOpts), { maxRetries: 2 });
          records = Array.isArray(result) ? result : [];
        } catch (e: any) {
          console.warn('[fetchPageLayoutTemplates] Traversal with index filter failed:', e?.message || e);
        }
      }
    }

    if (records.length === 0 && !filters?.objectTypeKey) {
      const allRecords = await retryWithBackoff(
        () => objectsApi.getObjectsInGraphByType(
          GobodoConfig.graph(),
          OBJECT_KEYS.PAGE_LAYOUT_TEMPLATE,
          { page: 0, size: 1000 }
        ),
        { maxRetries: 2 }
      );
      records = allRecords || [];
    }

    if (filters?.application && records.length > 0) {
      const targetApp = normalizeAppName(filters.application);
      records = records.filter((r: any) => {
        const props = r.properties || r.data || {};
        return normalizeAppName(props.application) === targetApp;
      });
    }

    const templates: PageLayoutTemplate[] = [];
    for (const record of records) {
      try {
        const sections = await fetchLayoutSections(String(record.id));
        templates.push(gobodoToTemplate(record, sections));
      } catch (error: any) {
        const gobodoError = handleGobodoError(error, { operation: 'fetchPageLayoutTemplates', templateId: String(record.id) });
        if (gobodoError) {
          logGobodoError(gobodoError, 'warn');
        }
      }
    }
    templates.sort((a, b) => a.name.localeCompare(b.name));
    return { data: templates };
  } catch (error: any) {
    const gobodoError = handleGobodoError(error, { operation: 'fetchPageLayoutTemplates' });
    if (gobodoError) {
      logGobodoError(gobodoError);
      throw gobodoError;
    }
    throw error;
  }
}

/**
 * Fetch single page layout template
 */
export async function fetchPageLayoutTemplate(templateId: string): Promise<{ data: PageLayoutTemplate }> {
  if (!USE_GOBODO_API) {
    throw new Error('Gobodo API not enabled. Set REACT_APP_USE_GOBODO_API=true');
  }

  const client = getGobodoClient();
  const objectsApi = new GobodoApi.GraphObjectsApi(client);

  try {
    const record = await retryWithBackoff(() => objectsApi.getObjectById(parseInt(templateId)), { maxRetries: 3 });
    const sections = await fetchLayoutSections(templateId);
    
    return { data: gobodoToTemplate(record, sections) };
  } catch (error: any) {
    console.error('Error fetching page layout template from Gobodo:', error);
    throw error;
  }
}

/**
 * Create page layout template.
 * When templateData.objectTypeKey is set, links the new layout to the object type via getObjectTypeAsObject and object_type_has_page_layout relationship (parentobjectid + relationshiptypekey).
 */
export async function createPageLayoutTemplate(
  templateData: Omit<PageLayoutTemplate, 'id' | 'sections' | 'createdAt' | 'updatedAt'>
): Promise<{ data: PageLayoutTemplate }> {
  if (!USE_GOBODO_API) {
    throw new Error('Gobodo API not enabled. Set REACT_APP_USE_GOBODO_API=true');
  }

  const client = getGobodoClient();
  const objectsApi = new GobodoApi.GraphObjectsApi(client);

  try {
    await ensureObjectTypeExists(OBJECT_KEYS.PAGE_LAYOUT_TEMPLATE, {
      apiName: OBJECT_KEYS.PAGE_LAYOUT_TEMPLATE,
      label: 'Page Layout Template',
      pluralLabel: 'Page Layout Templates',
      description: 'Template for page layouts',
      icon: 'FileText',
      category: 'system',
    });

    const objectTypesApi = new GobodoApi.GraphObjectTypesApi(client);
    const objectType = await retryWithBackoff(() => objectTypesApi.getObjectTypeByKey(OBJECT_KEYS.PAGE_LAYOUT_TEMPLATE, { maxFields: 0 }), { maxRetries: 3 });

    const payload = new GobodoApi.GraphObjectPayload();
    payload.typekey = OBJECT_KEYS.PAGE_LAYOUT_TEMPLATE;
    payload.typeid = objectType.id as any;
    payload.label = templateData.name;
    payload.graphs = [GobodoConfig.graph()];

    const templateProps = templateToGobodo(templateData);
    if (!templateProps.application) {
      templateProps.application = 'global';
    }
    payload.properties = templateProps;

    const objectTypeKey = templateData.objectTypeKey;
    if (objectTypeKey) {
      const parentObjectId = await getObjectTypeAsGraphObjectId(objectTypeKey);
      if (parentObjectId != null) {
        payload.parentobjectid = parentObjectId;
        payload.relationshiptypekey = getObjectTypePageLayoutRelKey();
      }
    }

    const response = await retryWithBackoff(() => objectsApi.createObject(payload), { maxRetries: 3 });
    return { data: gobodoToTemplate(response, []) };
  } catch (error: any) {
    const gobodoError = handleGobodoError(error, { operation: 'createPageLayoutTemplate' });
    if (gobodoError) {
      logGobodoError(gobodoError);
      throw gobodoError;
    }
    throw error;
  }
}

/**
 * Update page layout template
 */
export async function updatePageLayoutTemplate(
  templateId: string,
  updates: Partial<PageLayoutTemplate>
): Promise<{ data: PageLayoutTemplate }> {
  if (!USE_GOBODO_API) {
    throw new Error('Gobodo API not enabled. Set REACT_APP_USE_GOBODO_API=true');
  }

  const client = getGobodoClient();
  const objectsApi = new GobodoApi.GraphObjectsApi(client);

  try {
    const existing = await retryWithBackoff(() => objectsApi.getObjectById(parseInt(templateId)), { maxRetries: 3 });
    const existingData = existing.data || existing.properties || {};
    
    const updatedData = {
      ...existingData,
      ...templateToGobodo(updates as Partial<PageLayoutTemplate>),
      updatedAt: new Date().toISOString()
    };

    const payload = new GobodoApi.GraphObjectPayload();
    payload.typekey = OBJECT_KEYS.PAGE_LAYOUT_TEMPLATE;
    payload.typeid = existing.typeid;
    payload.label = updatedData.name;
    payload.graphs = [GobodoConfig.graph()];
    payload.properties = updatedData;

    const response = await retryWithBackoff(() => objectsApi.updateObject(parseInt(templateId), payload), { maxRetries: 3 });
    const sections = await fetchLayoutSections(templateId);
    
    return { data: gobodoToTemplate(response, sections) };
  } catch (error: any) {
    console.error('Error updating page layout template in Gobodo:', error);
    throw error;
  }
}

/**
 * Delete page layout template
 */
export async function deletePageLayoutTemplate(templateId: string): Promise<void> {
  if (!USE_GOBODO_API) {
    throw new Error('Gobodo API not enabled. Set REACT_APP_USE_GOBODO_API=true');
  }

  const client = getGobodoClient();
  const objectsApi = new GobodoApi.GraphObjectsApi(client);

  try {
    await retryWithBackoff(() => objectsApi.deleteObject(parseInt(templateId)), { maxRetries: 3 });
  } catch (error: any) {
    console.error('Error deleting page layout template from Gobodo:', error);
    throw error;
  }
}

// ============================================
// LAYOUT SECTION OPERATIONS
// ============================================

/**
 * Fetch layout sections for a template.
 * Uses CONTAINER INCOMING traversal from layout when templateId is numeric (sections created with parentobjectid = layout). Fallback: getObjectsInGraphByType + filter.
 */
export async function fetchLayoutSections(templateId: string): Promise<PageLayoutSection[]> {
  if (!USE_GOBODO_API) {
    throw new Error('Gobodo API not enabled. Set REACT_APP_USE_GOBODO_API=true');
  }

  const client = getGobodoClient();
  const objectsApi = new GobodoApi.GraphObjectsApi(client);
  const templateIdNum = /^\d+$/.test(String(templateId).trim()) ? parseInt(templateId, 10) : null;

  try {
    let records: any[] = [];

    if (templateIdNum != null) {
      const traversalApi = new GobodoApi.GraphTraversalApi(client);
      const input = new GobodoApi.GraphTraversalInput();
      input.setGraphId(GobodoConfig.graph());
      input.setStartObjectId(templateIdNum);
      input.setRelationshipTypes(['CONTAINER']);
      input.setDirection(GobodoApi.GraphTraversalInput.DirectionEnum.INCOMING);
      input.setEndObjectTypes([OBJECT_KEYS.PAGE_LAYOUT_SECTION]);
      input.setMaxPathLength(1);
      const cacheOpts = layoutSectionsTraversalCacheOpts(templateId);
      const result = await retryWithBackoff(() => traversalApi.traverseObjects(input, cacheOpts), { maxRetries: 3 });
      records = Array.isArray(result) ? result : [];
    }

    if (records.length === 0 && templateIdNum == null) {
      const allRecords = await retryWithBackoff(() => objectsApi.getObjectsInGraphByType(
        GobodoConfig.graph(),
        OBJECT_KEYS.PAGE_LAYOUT_SECTION,
        { page: 0, size: 1000 }
      ), { maxRetries: 3 });
      const templateIdStr = String(templateId);
      records = (allRecords || []).filter((r: any) => {
        const props = r.properties || r.data || {};
        return String(props.templateId ?? props.TemplateId__c) === templateIdStr;
      });
    }

    const sections: PageLayoutSection[] = [];
    for (const record of records) {
      const fields = await fetchSectionFields(String(record.id));
      const section = gobodoToSection(record, fields);
      if (section.visible === false) continue;
      sections.push(section);
    }
    sections.sort((a, b) => a.order - b.order);

    // Deduplicate ghost sections by stable slot signature (name+tab+page+order).
    // This avoids collapsing distinct sections that share a name across tabs.
    const deduped: PageLayoutSection[] = [];
    const seenSignatures = new Map<string, number>();
    for (const section of sections) {
      const key = [
        section.name.trim().toLowerCase(),
        String(section.tab ?? 'details').trim().toLowerCase(),
        String(section.page ?? 'default').trim().toLowerCase(),
        String(section.order ?? 0),
      ].join('|');
      const existingIdx = seenSignatures.get(key);
      if (existingIdx !== undefined) {
        // Keep whichever has more fields
        if (section.fields.length > deduped[existingIdx].fields.length) {
          console.log(`[fetchLayoutSections] Dedup: replacing "${deduped[existingIdx].name}" (${deduped[existingIdx].fields.length} fields) with (${section.fields.length} fields)`);
          deduped[existingIdx] = section;
        }
      } else {
        seenSignatures.set(key, deduped.length);
        deduped.push(section);
      }
    }
    if (deduped.length < sections.length) {
      console.log(`[fetchLayoutSections] Deduped ${sections.length} → ${deduped.length} sections`);
    }
    return deduped;
  } catch (error: any) {
    console.error('Error fetching layout sections from Gobodo:', error);
    throw error;
  }
}

/**
 * Create layout section.
 * When preResolvedSectionTypeId is provided (e.g. from syncLayoutSections batch), skips ensureObjectTypeExists and getObjectTypeByKey to avoid repeated backend calls.
 */
export async function createLayoutSection(
  layoutId: string,
  sectionData: Omit<PageLayoutSection, 'id' | 'fields' | 'createdAt' | 'updatedAt'>,
  preResolvedSectionTypeId?: number
): Promise<{ data: PageLayoutSection }> {
  if (!USE_GOBODO_API) {
    throw new Error('Gobodo API not enabled. Set REACT_APP_USE_GOBODO_API=true');
  }

  const client = getGobodoClient();

  try {
    let typeid: number;
    if (preResolvedSectionTypeId != null) {
      typeid = preResolvedSectionTypeId;
    } else {
      await ensureObjectTypeExists(OBJECT_KEYS.PAGE_LAYOUT_SECTION, {
        apiName: OBJECT_KEYS.PAGE_LAYOUT_SECTION,
        label: 'Page Layout Section',
        pluralLabel: 'Page Layout Sections',
        description: 'Section within a page layout',
        icon: 'Layout',
        category: 'system',
      });
      const objectTypesApi = new GobodoApi.GraphObjectTypesApi(client);
      const objectType = await retryWithBackoff(() => objectTypesApi.getObjectTypeByKey(OBJECT_KEYS.PAGE_LAYOUT_SECTION, { maxFields: 0 }), { maxRetries: 3 });
      typeid = objectType.id as number;
    }

    const layoutIdNum = /^\d+$/.test(String(layoutId)) ? parseInt(layoutId, 10) : null;

    const props = sectionToGobodo(sectionData, layoutId);
    const tab = String(props.tab ?? 'details').trim();
    const page = String(props.page ?? 'default').trim();

    const body: Record<string, any> = {
      typekey: OBJECT_KEYS.PAGE_LAYOUT_SECTION,
      typeid,
      label: sectionData.name,
      graphs: [GobodoConfig.graph()],
      properties: { ...props, tab, page, Tab__c: tab, Page__c: page },
      tab,
      page,
    };
    if (layoutIdNum != null) {
      body.parentobjectid = layoutIdNum;
      body.relationshiptypekey = 'CONTAINER';
    }

    const response = await retryWithBackoff(
      () => (client as any).callApi(
        '/object', 'POST',
        {}, {}, {}, {}, body,
        ['NexusGraphAPIAuth', 'NexusGraphAPIKey'],
        ['application/json'], ['application/json'],
        GobodoApi.GraphObjectPayload, null
      ),
      { maxRetries: 3 }
    );
    
    return { data: gobodoToSection(response, []) };
  } catch (error: any) {
    const gobodoError = handleGobodoError(error, { operation: 'createLayoutSection' });
    if (gobodoError) {
      logGobodoError(gobodoError);
      throw gobodoError;
    }
    throw error;
  }
}

/**
 * Update layout section
 */
export async function updateLayoutSection(
  layoutId: string,
  sectionId: string,
  updates: Partial<PageLayoutSection>
): Promise<{ data: PageLayoutSection }> {
  if (!USE_GOBODO_API) {
    throw new Error('Gobodo API not enabled. Set REACT_APP_USE_GOBODO_API=true');
  }

  const client = getGobodoClient();
  const objectsApi = new GobodoApi.GraphObjectsApi(client);

  try {
    const existing = await retryWithBackoff(() => objectsApi.getObjectById(parseInt(sectionId)), { maxRetries: 3 });
    const existingData = existing.data || existing.properties || {};
    
    const updatedData = {
      ...existingData,
      ...sectionToGobodo(updates as Partial<PageLayoutSection>, layoutId),
      updatedAt: new Date().toISOString()
    };

    const payload = new GobodoApi.GraphObjectPayload();
    payload.typekey = OBJECT_KEYS.PAGE_LAYOUT_SECTION;
    payload.typeid = existing.typeid;
    payload.label = updatedData.name;
    payload.graphs = [GobodoConfig.graph()];
    payload.properties = updatedData;

    const response = await retryWithBackoff(() => objectsApi.updateObject(parseInt(sectionId), payload), { maxRetries: 3 });
    const fields = await fetchSectionFields(sectionId);
    
    return { data: gobodoToSection(response, fields) };
  } catch (error: any) {
    console.error('Error updating layout section in Gobodo:', error);
    throw error;
  }
}

/**
 * Delete layout section
 */
export async function deleteLayoutSection(layoutId: string, sectionId: string): Promise<void> {
  if (!USE_GOBODO_API) {
    throw new Error('Gobodo API not enabled. Set REACT_APP_USE_GOBODO_API=true');
  }

  const client = getGobodoClient();
  const objectsApi = new GobodoApi.GraphObjectsApi(client);

  try {
    await retryWithBackoff(() => objectsApi.deleteObject(parseInt(sectionId)), { maxRetries: 3 });
  } catch (error: any) {
    console.error('Error deleting layout section from Gobodo:', error);
    throw error;
  }
}

// ============================================
// SECTION FIELD OPERATIONS
// ============================================

/**
 * Fetch fields for a section.
 * Uses CONTAINER INCOMING traversal from section when sectionId is numeric (fields created with parentobjectid = section). Fallback: getObjectsInGraphByType + filter.
 */
export async function fetchSectionFields(sectionId: string): Promise<PageLayoutField[]> {
  if (!USE_GOBODO_API) {
    throw new Error('Gobodo API not enabled. Set REACT_APP_USE_GOBODO_API=true');
  }

  const client = getGobodoClient();
  const objectsApi = new GobodoApi.GraphObjectsApi(client);
  const sectionIdNum = /^\d+$/.test(String(sectionId).trim()) ? parseInt(sectionId, 10) : null;

  try {
    let records: any[] = [];

    if (sectionIdNum != null) {
      const traversalApi = new GobodoApi.GraphTraversalApi(client);
      const input = new GobodoApi.GraphTraversalInput();
      input.setGraphId(GobodoConfig.graph());
      input.setStartObjectId(sectionIdNum);
      input.setRelationshipTypes(['CONTAINER']);
      input.setDirection(GobodoApi.GraphTraversalInput.DirectionEnum.INCOMING);
      input.setEndObjectTypes([OBJECT_KEYS.PAGE_LAYOUT_FIELD]);
      input.setMaxPathLength(1);
      const cacheOpts = sectionFieldsTraversalCacheOpts(sectionId);
      const result = await retryWithBackoff(() => traversalApi.traverseObjects(input, cacheOpts), { maxRetries: 3 });
      records = Array.isArray(result) ? result : [];
    }

    if (records.length === 0 && sectionIdNum == null) {
      const allRecords = await retryWithBackoff(() => objectsApi.getObjectsInGraphByType(
        GobodoConfig.graph(),
        OBJECT_KEYS.PAGE_LAYOUT_FIELD,
        { page: 0, size: 1000 }
      ), { maxRetries: 3 });
      const sectionIdStr = String(sectionId);
      records = (allRecords || []).filter((r: any) => {
        const props = r.properties || r.data || {};
        return String(props.SectionId__c ?? props.sectionId) === sectionIdStr;
      });
    }

    const fields = records.map((r: any) => gobodoToField(r));
    fields.sort((a: PageLayoutField, b: PageLayoutField) => a.order - b.order);

    // Deduplicate duplicate field rows returned by traversal/index on some environments.
    const deduped = new Map<string, PageLayoutField>();
    const normalizedFieldBase = (value: string) =>
      value
        .trim()
        .toLowerCase()
        .replace(/(__stg3_c|__stg_c|__dev_c|__c)$/i, '');
    const normalizedLabel = (value: string) =>
      value.trim().toLowerCase().replace(/\s+/g, ' ');

    for (const field of fields) {
      const idKey = normalizedFieldBase(String(field.fieldKey || field.fieldId || ''));
      const labelKey = normalizedLabel(String(field.label || ''));
      const key = idKey || labelKey;
      if (!key) continue;

      const existing = deduped.get(key);
      if (!existing) {
        deduped.set(key, field);
      } else {
        // Prefer record with explicit key/id metadata; fallback to lower order.
        const existingScore = Number(Boolean(existing.fieldKey || existing.fieldId)) * 10 + (1000 - (existing.order ?? 0));
        const nextScore = Number(Boolean(field.fieldKey || field.fieldId)) * 10 + (1000 - (field.order ?? 0));
        if (nextScore > existingScore) {
          deduped.set(key, field);
        }
      }
    }
    return Array.from(deduped.values()).sort((a, b) => a.order - b.order);
  } catch (error: any) {
    console.error('Error fetching section fields from Gobodo:', error);
    throw error;
  }
}

/**
 * Add field to section.
 * When preResolvedFieldTypeId is provided (e.g. from syncLayoutSections batch), skips ensureObjectTypeExists and getObjectTypeByKey to avoid repeated backend calls. Does not set fields on the object type—only creates graph objects under the section.
 */
export async function addFieldToSection(
  layoutId: string,
  sectionId: string,
  fieldData: Partial<PageLayoutField>,
  preResolvedFieldTypeId?: number
): Promise<{ data: PageLayoutField }> {
  if (!USE_GOBODO_API) {
    throw new Error('Gobodo API not enabled. Set REACT_APP_USE_GOBODO_API=true');
  }

  const client = getGobodoClient();

  try {
    let typeid: number;
    if (preResolvedFieldTypeId != null) {
      typeid = preResolvedFieldTypeId;
    } else {
      await ensureObjectTypeExists(OBJECT_KEYS.PAGE_LAYOUT_FIELD, {
        apiName: OBJECT_KEYS.PAGE_LAYOUT_FIELD,
        label: 'Page Layout Field',
        pluralLabel: 'Page Layout Fields',
        description: 'Field within a page layout section',
        icon: 'FileText',
        category: 'system',
      });
      const objectTypesApi = new GobodoApi.GraphObjectTypesApi(client);
      const objectType = await retryWithBackoff(() => objectTypesApi.getObjectTypeByKey(OBJECT_KEYS.PAGE_LAYOUT_FIELD, { maxFields: 0 }), { maxRetries: 3 });
      typeid = objectType.id as number;
    }

    const properties = fieldToGobodo(fieldData, sectionId);
    const topLevelLabel = String(properties.label || properties.fieldKey || 'field').trim();
    const sectionIdNum = /^\d+$/.test(String(sectionId)) ? parseInt(sectionId, 10) : null;

    const body: Record<string, any> = {
      typekey: OBJECT_KEYS.PAGE_LAYOUT_FIELD,
      typeid,
      label: topLevelLabel,
      graphs: [GobodoConfig.graph()],
      properties: properties,
      invalidateCacheKeys: sectionFieldMutationCacheKeys(layoutId, sectionId),
    };
    if (sectionIdNum != null) {
      body.parentobjectid = sectionIdNum;
      body.relationshiptypekey = 'CONTAINER';
    }

    const response = await retryWithBackoff(
      () => (client as any).callApi(
        '/object', 'POST',
        {}, {}, {}, {}, body,
        ['NexusGraphAPIAuth', 'NexusGraphAPIKey'],
        ['application/json'], ['application/json'],
        GobodoApi.GraphObjectPayload, null
      ),
      { maxRetries: 2 }
    );
    return { data: gobodoToField(response) };
  } catch (error: any) {
    // Log the full error details for debugging field creation 500s
    const body = error?.response?.body ?? error?.response?.text ?? error?.response?.data ?? error?.body;
    const status = error?.response?.status ?? error?.status;
    console.error(`[addFieldToSection] FAILED (${status}):`, {
      payload: { typekey: OBJECT_KEYS.PAGE_LAYOUT_FIELD, label: fieldData.label || fieldData.fieldKey, sectionId },
      properties: fieldData,
      errorBody: typeof body === 'string' ? body.slice(0, 500) : body,
      errorMessage: error?.message,
    });
    const gobodoError = handleGobodoError(error, { operation: 'addFieldToSection' });
    if (gobodoError) {
      logGobodoError(gobodoError);
      throw gobodoError;
    }
    throw error;
  }
}

/**
 * Fetch section field records including Gobodo record id (for delete/sync).
 * Uses CONTAINER INCOMING traversal from section when sectionId is numeric (fields have parentobjectid = section); fallback: getObjectsInGraphByType + filter.
 */
export async function fetchSectionFieldRecords(sectionId: string): Promise<Array<PageLayoutField & { id: string }>> {
  if (!USE_GOBODO_API) {
    throw new Error('Gobodo API not enabled. Set REACT_APP_USE_GOBODO_API=true');
  }
  const client = getGobodoClient();
  const objectsApi = new GobodoApi.GraphObjectsApi(client);
  const sectionIdNum = /^\d+$/.test(String(sectionId).trim()) ? parseInt(sectionId, 10) : null;

  let records: any[] = [];
  if (sectionIdNum != null) {
    const traversalApi = new GobodoApi.GraphTraversalApi(client);
    const input = new GobodoApi.GraphTraversalInput();
    input.setGraphId(GobodoConfig.graph());
    input.setStartObjectId(sectionIdNum);
    input.setRelationshipTypes(['CONTAINER']);
    input.setDirection(GobodoApi.GraphTraversalInput.DirectionEnum.INCOMING);
    input.setEndObjectTypes([OBJECT_KEYS.PAGE_LAYOUT_FIELD]);
    input.setMaxPathLength(1);
    const cacheOpts = sectionFieldsTraversalCacheOpts(sectionId);
    const result = await retryWithBackoff(() => traversalApi.traverseObjects(input, cacheOpts), { maxRetries: 3 });
    records = Array.isArray(result) ? result : [];
  }
  if (records.length === 0 && sectionIdNum == null) {
    const allRecords = await retryWithBackoff(() => objectsApi.getObjectsInGraphByType(
      GobodoConfig.graph(),
      OBJECT_KEYS.PAGE_LAYOUT_FIELD,
      { page: 0, size: 1000 }
    ), { maxRetries: 3 });
    const sectionIdStr = String(sectionId);
    records = (allRecords || []).filter((r: any) => {
      const props = r.properties || r.data || {};
      return String(props.SectionId__c ?? props.sectionId) === sectionIdStr;
    });
  }
  return records.map((r: any) => ({ ...gobodoToField(r), id: String(r.id) }));
}

/**
 * Remove field from section
 */
export async function removeFieldFromSection(
  layoutId: string,
  sectionId: string,
  fieldId: string
): Promise<void> {
  if (!USE_GOBODO_API) {
    throw new Error('Gobodo API not enabled. Set REACT_APP_USE_GOBODO_API=true');
  }

  const client = getGobodoClient();
  const objectsApi = new GobodoApi.GraphObjectsApi(client);

  try {
    await retryWithBackoff(() => objectsApi.deleteObject(parseInt(fieldId)), { maxRetries: 3 });
  } catch (error: any) {
    console.error('Error removing field from section in Gobodo:', error);
    throw error;
  }
}

/**
 * Update a section field (order, label, required, fieldKey, etc.).
 * Used by incremental sync and can be used by reorderSectionFields.
 */
export async function updateSectionField(
  layoutId: string,
  sectionId: string,
  fieldId: string,
  updates: Partial<PageLayoutField>
): Promise<{ data: PageLayoutField }> {
  if (!USE_GOBODO_API) {
    throw new Error('Gobodo API not enabled. Set REACT_APP_USE_GOBODO_API=true');
  }

  const client = getGobodoClient();
  const objectsApi = new GobodoApi.GraphObjectsApi(client);

  const existing = await retryWithBackoff(() => objectsApi.getObjectById(parseInt(fieldId)), { maxRetries: 3 });
  const existingData = existing.data || existing.properties || {};
  const existingField = gobodoToField(existing);
  const merged: Partial<PageLayoutField> = { ...existingField, ...updates };
  const properties = fieldToGobodo(merged, sectionId);
  const updatedData = {
    ...existingData,
    ...properties,
    updatedAt: new Date().toISOString(),
  };

  const payload = new GobodoApi.GraphObjectPayload();
  payload.typekey = OBJECT_KEYS.PAGE_LAYOUT_FIELD;
  payload.typeid = existing.typeid;
  payload.label = String(merged.label ?? merged.fieldKey ?? updatedData.label ?? '');
  payload.graphs = [GobodoConfig.graph()];
  payload.properties = updatedData;

  const response = await retryWithBackoff(() => objectsApi.updateObject(parseInt(fieldId), payload), { maxRetries: 3 });
  return { data: gobodoToField(response) };
}

/**
 * Sync section/field tree for a layout template (incremental: create new, update existing, delete removed).
 * Used when saving from Visual Layout Builder.
 */
export async function syncLayoutSections(
  layoutId: string,
  sections: Array<{
    id: string;
    label: string;
    order: number;
    columns: 1 | 2;
    collapsible: boolean;
    collapsed?: boolean;
    fields: Array<{ fieldId: string; fieldName: string; fieldLabel: string; order: number; required: boolean }>;
  }>
): Promise<void> {
  if (!USE_GOBODO_API) return;
  const client = getGobodoClient();
  const objectsApi = new GobodoApi.GraphObjectsApi(client);

  // Fetch existing state from Gobodo
  const existingSections = await fetchLayoutSections(layoutId);

  // Build lookup maps: by Gobodo numeric ID AND by normalized name
  // This handles both cases: UI sending Gobodo IDs (numeric) or local IDs (section_xxx)
  const existingById = new Map<string, typeof existingSections[0]>();
  const existingByName = new Map<string, typeof existingSections[0]>();
  for (const s of existingSections) {
    existingById.set(s.id, s);
    existingByName.set(s.name.trim().toLowerCase(), s);
  }

  // Determine which existing sections will be kept (matched by ID or name)
  const payloadNameSet = new Set(sections.map((s) => s.label.trim().toLowerCase()));
  const payloadIdSet = new Set(sections.map((s) => s.id));
  const sectionsToKeep = existingSections.filter(
    (s) => payloadIdSet.has(s.id) || payloadNameSet.has(s.name.trim().toLowerCase())
  );

  // Only fetch field records for sections we'll keep (not ones we're deleting)
  const existingFieldRecordsBySection: Record<string, Array<PageLayoutField & { id: string }>> = {};
  for (const s of sectionsToKeep) {
    try {
      existingFieldRecordsBySection[s.id] = await fetchSectionFieldRecords(s.id);
    } catch (e: any) {
      console.warn(`[syncLayoutSections] Failed to fetch fields for section ${s.id}, treating as empty:`, e?.message);
      existingFieldRecordsBySection[s.id] = [];
    }
  }

  // Resolve type IDs once (not per section/field)
  let sectionTypeId: number | undefined;
  let fieldTypeId: number | undefined;

  const needsNewSections = sections.some((sec) => {
    const match = existingById.get(sec.id) || existingByName.get(sec.label.trim().toLowerCase());
    return !match;
  });
  const needsNewFields = sections.some((sec) => (sec.fields?.length ?? 0) > 0);

  if (needsNewSections || needsNewFields) {
    const objectTypesApi = new GobodoApi.GraphObjectTypesApi(client);
    if (needsNewSections) {
      await ensureObjectTypeExists(OBJECT_KEYS.PAGE_LAYOUT_SECTION, {
        apiName: OBJECT_KEYS.PAGE_LAYOUT_SECTION,
        label: 'Page Layout Section',
        pluralLabel: 'Page Layout Sections',
        description: 'Section within a page layout',
        icon: 'Layout',
        category: 'system',
      });
      const sectionType = await retryWithBackoff(() => objectTypesApi.getObjectTypeByKey(OBJECT_KEYS.PAGE_LAYOUT_SECTION, { maxFields: 0 }), { maxRetries: 3 });
      sectionTypeId = sectionType.id as number;
    }
    if (needsNewFields) {
      await ensureObjectTypeExists(OBJECT_KEYS.PAGE_LAYOUT_FIELD, {
        apiName: OBJECT_KEYS.PAGE_LAYOUT_FIELD,
        label: 'Page Layout Field',
        pluralLabel: 'Page Layout Fields',
        description: 'Field within a page layout section',
        icon: 'FileText',
        category: 'system',
      });
      const fieldType = await retryWithBackoff(() => objectTypesApi.getObjectTypeByKey(OBJECT_KEYS.PAGE_LAYOUT_FIELD, { maxFields: 0 }), { maxRetries: 3 });
      fieldTypeId = fieldType.id as number;
    }
  }

  // Track which existing Gobodo section IDs are still in the payload (for deletion phase)
  const matchedGobodoSectionIds = new Set<string>();

  // Phase 1: Upsert sections and their fields
  console.log(`[syncLayoutSections] Syncing ${sections.length} sections for layout ${layoutId}`);

  for (const sec of sections) {
    // Existing numeric IDs are authoritative. Name fallback is only for local temp sections.
    const hasNumericId = /^\d+$/.test(String(sec.id).trim());
    const existingSection = hasNumericId
      ? existingById.get(sec.id)
      : existingByName.get(sec.label.trim().toLowerCase());

    if (existingSection) {
      // UPDATE existing section
      matchedGobodoSectionIds.add(existingSection.id);
      const gobodoSectionId = existingSection.id;

      // Only update metadata if something changed
      const metaChanged = existingSection.name !== sec.label
        || existingSection.order !== sec.order
        || existingSection.columns !== sec.columns
        || existingSection.collapsible !== sec.collapsible
        || existingSection.defaultCollapsed !== (sec.collapsed ?? false);

      if (metaChanged) {
        try {
          await updateLayoutSection(layoutId, gobodoSectionId, {
            name: sec.label,
            order: sec.order,
            columns: sec.columns as 1 | 2 | 3 | 4,
            collapsible: sec.collapsible,
            defaultCollapsed: sec.collapsed ?? false,
          });
        } catch (e: any) {
          console.warn(`[syncLayoutSections] Failed to update section ${gobodoSectionId}:`, e?.message || e);
        }
      }

      // Sync fields within this section
      const existingFieldRecords = existingFieldRecordsBySection[gobodoSectionId] || [];
      const existingFieldsByKey = new Map<string, Array<typeof existingFieldRecords[0]>>();
      for (const f of existingFieldRecords) {
        const key = (f.fieldKey || f.fieldId || '').toString().trim().toLowerCase();
        if (!key) continue;
        const bucket = existingFieldsByKey.get(key) || [];
        bucket.push(f);
        existingFieldsByKey.set(key, bucket);
      }

      for (let fieldIndex = 0; fieldIndex < (sec.fields || []).length; fieldIndex++) {
        const f = sec.fields[fieldIndex];
        const fieldKey = (f.fieldName ?? f.fieldId ?? `field-${f.order ?? 0}`).toString().trim();
        const normalizedKey = fieldKey.toLowerCase();
        const normalizedOrder = fieldIndex + 1;

        const bucket = existingFieldsByKey.get(normalizedKey) || [];
        const existingField = bucket.shift();
        if (bucket.length > 0) existingFieldsByKey.set(normalizedKey, bucket);
        else existingFieldsByKey.delete(normalizedKey);

        if (existingField) {
          // Only update if order/label/required changed
          const fieldChanged = existingField.order !== normalizedOrder
            || existingField.label !== (f.fieldLabel ?? '')
            || existingField.required !== (f.required ?? false);

          if (fieldChanged) {
            try {
              await updateSectionField(layoutId, gobodoSectionId, existingField.id, {
                order: normalizedOrder,
                label: f.fieldLabel ?? '',
                required: f.required ?? false,
                fieldKey,
              });
            } catch (e: any) {
              console.warn(`[syncLayoutSections] Failed to update field ${existingField.id}:`, e?.message || e);
            }
          }
        } else {
          // Add new field
          try {
            await addFieldToSection(layoutId, gobodoSectionId, {
              fieldKey,
              label: f.fieldLabel ?? '',
              fieldId: fieldKey,
              order: normalizedOrder,
              required: f.required ?? false,
              type: 'text',
            }, fieldTypeId);
          } catch (e: any) {
            console.error(`[syncLayoutSections] Failed to add field "${fieldKey}" to section "${sec.label}":`, e?.message || e);
          }
        }
      }

      // Remove fields no longer in the payload
      for (const staleFieldRecords of Array.from(existingFieldsByKey.values())) {
        for (const existingField of staleFieldRecords) {
          try {
            await removeFieldFromSection(layoutId, gobodoSectionId, existingField.id);
          } catch (e: any) {
            console.warn(`[syncLayoutSections] Failed to remove field ${existingField.id}:`, e?.message || e);
          }
        }
      }
    } else {
      // CREATE new section + fields
      try {
        const { data: created } = await createLayoutSection(layoutId, {
          name: sec.label,
          order: sec.order,
          columns: sec.columns as 1 | 2 | 3 | 4,
          collapsible: sec.collapsible,
          defaultCollapsed: sec.collapsed ?? false,
          visible: true,
          page: 'default',
          tab: 'details',
          createdBy: 'system',
        }, sectionTypeId);
        matchedGobodoSectionIds.add(created.id);

        for (let j = 0; j < (sec.fields || []).length; j++) {
          const f = sec.fields![j];
          const fieldKey = (f.fieldName ?? f.fieldId ?? `field-${f.order ?? j}`).toString().trim() || `field-${j}`;
          try {
            await addFieldToSection(layoutId, created.id, {
              fieldKey,
              label: f.fieldLabel ?? '',
              fieldId: fieldKey,
              order: f.order ?? j,
              required: f.required ?? false,
              type: 'text',
            }, fieldTypeId);
          } catch (e: any) {
            console.error(`[syncLayoutSections] Failed to add field "${fieldKey}" to section "${sec.label}":`, e?.message || e);
          }
        }
      } catch (e: any) {
        console.error(`[syncLayoutSections] Failed to create section "${sec.label}":`, e?.message || e);
      }
    }
  }

  // Phase 2: Delete sections that were removed from the payload
  // Note: Gobodo's deleteObject endpoint has a known backend bug (NPE/500) for some records.
  // We attempt deletion but don't block on failures — orphaned sections are hidden by
  // deduplication in fetchLayoutSections.
  const sectionsToDelete = existingSections.filter(s => !matchedGobodoSectionIds.has(s.id));
  if (sectionsToDelete.length > 0) {
    console.log(`[syncLayoutSections] Attempting to delete ${sectionsToDelete.length} removed section(s)`);
    for (const existingSection of sectionsToDelete) {
      try {
        const fieldRecords = await fetchSectionFieldRecords(existingSection.id);
        for (const fr of fieldRecords) {
          try {
            await retryWithBackoff(() => objectsApi.deleteObject(parseInt(fr.id, 10)), { maxRetries: 3 });
          } catch (e: any) {
            console.warn(`[syncLayoutSections] Failed to delete field ${fr.id}:`, e?.message || e);
          }
        }
        await deleteLayoutSection(layoutId, existingSection.id);
        console.log(`[syncLayoutSections] Deleted section "${existingSection.name}" (${existingSection.id})`);
      } catch (e: any) {
        // Some environments return 500/NPE on delete. Attempt soft-hide as fallback.
        try {
          await updateLayoutSection(layoutId, existingSection.id, { visible: false });
          console.warn(`[syncLayoutSections] Delete failed for section ${existingSection.id}; section was soft-hidden instead.`);
        } catch {
          console.warn(`[syncLayoutSections] Delete failed for section ${existingSection.id} and soft-hide also failed.`);
        }
      }
    }
  }

  console.log(`[syncLayoutSections] Sync complete for layout ${layoutId}`);
}

/**
 * Reorder section fields
 */
export async function reorderSectionFields(
  layoutId: string,
  sectionId: string,
  fieldOrders: Array<{ fieldId: string; order: number }>
): Promise<void> {
  if (!USE_GOBODO_API) {
    throw new Error('Gobodo API not enabled. Set REACT_APP_USE_GOBODO_API=true');
  }

  for (const { fieldId, order } of fieldOrders) {
    await updateSectionField(layoutId, sectionId, fieldId, { order });
  }
}
